import { useEffect, useRef, useState } from 'react';
import { useToast } from '../components/Toast';
import { useModal } from '../components/ModalProvider';
import { fmtDate } from '../utils/format';
import { custAnnOptions, custAnnList, custAnnPreview, custAnnRecipients, custAnnCreate } from '../api/announcements';
import { searchCustomers } from '../api/customers';
import { TAG_GROUPS, TAG_INFO } from '../config/customerTags';

// Modal xem danh sách KH sẽ nhận + bỏ bớt vài khách (loại trừ).
function RecipientsModal({ scope, excluded, setExcluded, onClose }) {
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  useEffect(() => { custAnnRecipients(scope).then(setData).catch(() => setData({ total: 0, list: [] })); }, []); // eslint-disable-line
  const ex = new Set(excluded);
  // Khoá loại trừ theo 'source|code' — mã socson & pmbox có thể TRÙNG nhưng là 2 KH khác nhau.
  const keyOf = (c) => `${c.source || 'socson'}|${c.code}`;
  const toggle = (key) => setExcluded((arr) => (arr.includes(key) ? arr.filter((c) => c !== key) : [...arr, key]));
  const list = (data?.list || []).filter((c) => !q.trim() || `${c.code} ${c.name} ${c.sale}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div>
      <h3>Danh sách khách hàng sẽ nhận</h3>
      {!data ? <div className="empty">Đang tải…</div> : (
        <>
          <div className="small muted" style={{ marginBottom: 8 }}>
            Bỏ tick để KHÔNG gửi cho khách đó. Sẽ gửi <b style={{ color: 'var(--accent)' }}>{data.list.length - excluded.filter((k) => data.list.some((x) => keyOf(x) === k)).length}</b>/{data.total}
            {data.total > data.list.length ? ` (hiển thị ${data.list.length} đầu)` : ''}.
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm trong danh sách…" style={{ marginBottom: 8 }} />
          <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
            <div className="tbl-wrap">
            <table>
              <thead><tr><th style={{ width: 40 }}>Gửi</th><th>Mã</th><th>Nguồn</th><th>Tên KH</th><th>Sale</th></tr></thead>
              <tbody>
                {list.map((c) => (
                  <tr key={keyOf(c)} style={ex.has(keyOf(c)) ? { opacity: 0.45 } : undefined}>
                    <td className="c"><input type="checkbox" checked={!ex.has(keyOf(c))} onChange={() => toggle(keyOf(c))} /></td>
                    <td className="mono">{c.code}</td>
                    <td className="small">{c.source === 'pmbox' ? 'PMBox' : 'SocSon'}</td>
                    <td>{c.name || '—'}</td>
                    <td className="small">{c.sale || '—'}</td>
                  </tr>
                ))}
                {!list.length && <tr><td colSpan={5} className="empty">Không có khách phù hợp</td></tr>}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}
      <div className="row" style={{ justifyContent: 'space-between', marginTop: 14 }}>
        <button className="btn ghost sm" onClick={() => setExcluded([])}>Chọn lại tất cả</button>
        <button className="btn" onClick={onClose}>Xong</button>
      </div>
    </div>
  );
}

function fmtDateTime(d) {
  const dt = new Date(d);
  return `${fmtDate(dt)} ${String(dt.getHours()).padStart(2, '0')}:${String(dt.getMinutes()).padStart(2, '0')}`;
}

const SCOPES = [
  ['all', 'Toàn công ty'],
  ['sale', 'Theo sale'],
  ['group', 'Theo nhóm KD'],
  ['tag', 'Theo tag'],
  ['custom', 'Chọn KH cụ thể'],
];

// 2 nguồn khách hàng khác nhau (socson + pmbox). Người gửi phải chọn ≥1.
const SOURCE_OPTS = [
  ['socson', 'SocSon'],
  ['pmbox', 'PMBox'],
];
const srcLabel = (s) => (s === 'pmbox' ? 'PMBox' : 'SocSon');

export default function CustomerAnnounce() {
  const toast = useToast();
  const { openModal, closeModal } = useModal();
  const [opts, setOpts] = useState({ sales: [], groups: [], year: new Date().getFullYear() });
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [type, setType] = useState('all');
  const [sources, setSources] = useState([]); // 'socson' | 'pmbox' — BẮT BUỘC chọn ≥1
  const [sales, setSales] = useState([]); // userId[]
  const [groups, setGroups] = useState([]); // groupId[]
  const [custs, setCusts] = useState([]); // {code, name}[]
  const [tagsSel, setTagsSel] = useState([]); // (type='tag') tag KH cần gửi
  const [excluded, setExcluded] = useState([]); // custCode[] bỏ bớt
  const [count, setCount] = useState(null);
  const [pv, setPv] = useState(null); // { count, socson, pmbox } — tách theo nguồn
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  const [q, setQ] = useState('');
  const [sug, setSug] = useState([]);

  const loadHistory = () => custAnnList().then(setHistory).catch(() => {});
  useEffect(() => { custAnnOptions().then(setOpts).catch(() => {}); loadHistory(); }, []);

  // Gợi ý KH khi gõ (cho "Chọn KH cụ thể").
  const qTimer = useRef(null);
  useEffect(() => {
    if (type !== 'custom' || !q.trim()) { setSug([]); return; }
    clearTimeout(qTimer.current);
    qTimer.current = setTimeout(() => { searchCustomers(q.trim()).then(setSug).catch(() => setSug([])); }, 300);
  }, [q, type]);

  const scopeOf = () => ({
    type,
    year: opts.year,
    sources,
    sales: type === 'sale' ? sales : [],
    groups: type === 'group' ? groups : [],
    tags: type === 'tag' ? tagsSel : [],
    custCodes: type === 'custom' ? custs.map((c) => c.code) : [],
    exclude: excluded,
  });

  // Đổi bộ lọc gốc -> xoá danh sách loại trừ (danh sách người nhận đã khác).
  useEffect(() => { setExcluded([]); }, [type, sources, sales, groups, custs, tagsSel]);

  // Đếm số KH nhận (debounce) mỗi khi đổi bộ lọc / nguồn / loại trừ.
  const pvTimer = useRef(null);
  useEffect(() => {
    setCount(null); setPv(null);
    if (!sources.length) { setCount(0); return; } // chưa chọn nguồn -> chưa đếm
    clearTimeout(pvTimer.current);
    pvTimer.current = setTimeout(() => {
      custAnnPreview(scopeOf()).then((r) => { setCount(r.count); setPv(r); }).catch(() => setCount(null));
    }, 400);
    // eslint-disable-next-line
  }, [type, sources, sales, groups, custs, tagsSel, excluded, opts.year]);

  const toggle = (arr, set, v) => set(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const addCust = (c) => { if (!custs.some((x) => x.code === c.code)) setCusts([...custs, { code: c.code, name: c.name }]); setQ(''); setSug([]); };

  async function send() {
    if (sending) return;
    if (!content.trim()) return toast('Nhập nội dung thông báo', 'warn');
    if (!sources.length) return toast('Chọn ít nhất 1 nguồn khách hàng (SocSon / PMBox)', 'warn');
    if (type === 'tag' && !tagsSel.length) return toast('Chọn ít nhất 1 tag', 'warn');
    if (type === 'sale' && !sales.length) return toast('Chọn ít nhất 1 sale', 'warn');
    if (type === 'group' && !groups.length) return toast('Chọn ít nhất 1 nhóm', 'warn');
    if (type === 'custom' && !custs.length) return toast('Chọn ít nhất 1 khách hàng', 'warn');
    if (!count) return toast('Không có khách hàng nào khớp bộ lọc', 'warn');
    setSending(true);
    try {
      await custAnnCreate({ title: title.trim(), content: content.trim(), scope: scopeOf() });
      toast(`Đang gửi thông báo tới ${count} khách hàng…`);
      setTitle(''); setContent('');
      setTimeout(loadHistory, 500);
    } catch (err) {
      toast(err.response?.data?.message || 'Có lỗi xảy ra', 'danger');
    } finally { setSending(false); }
  }

  return (
    <div className="card">
      <h3 style={{ marginTop: 0 }}>📣 Thông báo gửi khách hàng</h3>
      <div className="small muted" style={{ marginBottom: 14 }}>
        Gửi vào hộp tin nhắn của khách hàng (kênh CSKH). KH thấy nhãn CSKH, không thấy tên thật.
      </div>

      <div className="field">
        <label>Tiêu đề (tuỳ chọn)</label>
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="VD: Thông báo lịch nghỉ lễ" />
      </div>
      <div className="field">
        <label>Nội dung gửi khách hàng</label>
        <textarea rows={3} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Nội dung thông báo gửi tới khách hàng…" />
      </div>

      <div className="field">
        <label>Nguồn khách hàng {sources.length ? `(${sources.map(srcLabel).join(' + ')})` : ''}</label>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {SOURCE_OPTS.map(([k, l]) => (
            <button type="button" key={k} className={`filterBtn${sources.includes(k) ? ' on' : ''}`} onClick={() => toggle(sources, setSources, k)}>
              {sources.includes(k) ? '✓ ' : ''}{l}
            </button>
          ))}
        </div>
        {!sources.length && <div className="small" style={{ color: 'var(--warn)', marginTop: 4 }}>Chọn ít nhất 1 nguồn (SocSon / PMBox / cả hai)</div>}
      </div>

      <div className="field">
        <label>Gửi tới</label>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {SCOPES.map(([k, l]) => (
            <button type="button" key={k} className={`filterBtn${type === k ? ' on' : ''}`} onClick={() => setType(k)}>{l}</button>
          ))}
        </div>
      </div>

      {type === 'sale' && (
        <div className="field">
          <label>Chọn sale ({sales.length})</label>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {opts.sales.map((s) => (
              <button type="button" key={s.id} className={`filterBtn${sales.includes(s.id) ? ' on' : ''}`} onClick={() => toggle(sales, setSales, s.id)}>
                {s.name}{s.group ? ` · ${s.group}` : ''}
              </button>
            ))}
            {!opts.sales.length && <span className="small muted">Không có sale nào</span>}
          </div>
        </div>
      )}

      {type === 'group' && (
        <div className="field">
          <label>Chọn nhóm kinh doanh ({groups.length})</label>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
            {opts.groups.map((g) => (
              <button type="button" key={g.id} className={`filterBtn${groups.includes(g.id) ? ' on' : ''}`} onClick={() => toggle(groups, setGroups, g.id)}>{g.name}</button>
            ))}
            {!opts.groups.length && <span className="small muted">Không có nhóm nào</span>}
          </div>
        </div>
      )}

      {type === 'tag' && (
        <div className="field">
          <label>Chọn tag ({tagsSel.length}) — cùng nhóm là HOẶC, khác nhóm là VÀ</label>
          {TAG_GROUPS.map((g) => (
            <div key={g.key} style={{ marginBottom: 8 }}>
              <div className="small muted" style={{ marginBottom: 4 }}>{g.label}</div>
              <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                {g.options.map((o) => {
                  const on = tagsSel.includes(o.v);
                  return (
                    <button type="button" key={o.v} onClick={() => toggle(tagsSel, setTagsSel, o.v)}
                      style={{ fontSize: 13, fontWeight: 600, padding: '6px 13px', borderRadius: 20, cursor: 'pointer',
                        border: `1px solid ${on ? o.color : 'color-mix(in srgb, var(--text3) 40%, transparent)'}`,
                        color: on ? '#fff' : 'var(--text3)', background: on ? o.color : 'transparent' }}>
                      {on ? '✓ ' : ''}{o.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {type === 'custom' && (
        <div className="field">
          <label>Chọn khách hàng ({custs.length})</label>
          <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginBottom: custs.length ? 8 : 0 }}>
            {custs.map((c) => (
              <span key={c.code} className="tag" style={{ cursor: 'pointer' }} onClick={() => setCusts(custs.filter((x) => x.code !== c.code))} title="Bỏ chọn">
                {c.code} · {c.name} ✕
              </span>
            ))}
          </div>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm theo mã / tên KH…" />
          {sug.length > 0 && (
            <div className="card" style={{ padding: 4, marginTop: 4, maxHeight: 180, overflowY: 'auto' }}>
              {sug.map((c) => (
                <div key={c.id} className="notif-row" style={{ cursor: 'pointer', padding: '6px 8px' }} onClick={() => addCust(c)}>
                  <div style={{ flex: 1 }}><b>{c.code}</b> · {c.name} {c.sale ? <span className="small muted">— {c.sale}</span> : null}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="row between" style={{ marginTop: 8, flexWrap: 'wrap', gap: 8 }}>
        <span className="small" style={{ color: 'var(--accent)' }}>
          {!sources.length ? <span className="muted">Chọn nguồn khách hàng để đếm</span> : (
            <>
              <b>{count == null ? '…' : count.toLocaleString('vi-VN')}</b> khách hàng sẽ nhận
              {pv && (pv.socson || pv.pmbox) ? (
                <span className="muted"> ({[pv.socson ? `${pv.socson.toLocaleString('vi-VN')} SocSon` : '', pv.pmbox ? `${pv.pmbox.toLocaleString('vi-VN')} PMBox` : ''].filter(Boolean).join(' + ')})</span>
              ) : type === 'all' && sources.includes('socson') ? <span className="muted"> (SocSon theo doanh thu năm {opts.year})</span> : null}
              {excluded.length > 0 && <span className="muted"> · đã bỏ {excluded.length}</span>}
            </>
          )}
        </span>
        <div className="row" style={{ gap: 8 }}>
          <button className="btn ghost" onClick={() => openModal(<RecipientsModal scope={scopeOf()} excluded={excluded} setExcluded={setExcluded} onClose={closeModal} />, { wide: true })} disabled={!count && !excluded.length}>
            Xem / bỏ bớt danh sách
          </button>
          <button className="btn" onClick={send} disabled={sending || !count}>{sending ? 'Đang gửi…' : '📤 Gửi thông báo'}</button>
        </div>
      </div>

      <h4 style={{ marginBottom: 8, marginTop: 22 }}>Đã gửi gần đây</h4>
      {!history.length ? <div className="empty">Chưa có thông báo nào</div> : history.map((a) => {
        const s = a.stats || {};
        return (
          <div key={a._id} className="notif-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13.5 }}>{a.title || a.content}</div>
              {a.title && <div className="small muted" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.content}</div>}
              <div className="small muted mono" style={{ marginTop: 2 }}>
                {scopeLabel(a.scope)} · {a.createdByName || '—'} · {fmtDateTime(a.createdAt)}
              </div>
            </div>
            <span className="mono small" style={{ flex: '0 0 auto', color: s.done ? 'var(--ok)' : 'var(--warn)' }}>
              {s.done ? '✓ ' : '⏳ '}{s.sent || 0}/{s.total || 0}{s.failed ? ` · lỗi ${s.failed}` : ''}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function scopeLabel(sc) {
  if (!sc) return 'Toàn công ty';
  const src = (sc.sources && sc.sources.length ? sc.sources : ['socson']).map((s) => (s === 'pmbox' ? 'PMBox' : 'SocSon')).join('+');
  let base;
  if (sc.type === 'sale') base = `${(sc.sales || []).length} sale`;
  else if (sc.type === 'group') base = `${(sc.groups || []).length} nhóm KD`;
  else if (sc.type === 'tag') base = `tag: ${(sc.tags || []).map((t) => TAG_INFO[t]?.short || t).join(', ') || '—'}`;
  else if (sc.type === 'custom') base = `${(sc.custCodes || []).length} KH`;
  else base = `Toàn công ty (DT ${sc.year || ''})`;
  return `[${src}] ${base}`;
}
